<template>
  <div id="app">
    <div v-if="!token" class="login-wrap">
      <el-card class="login-card">
        <h2>同城机器人后台</h2>
        <el-form @submit.native.prevent="login">
          <el-form-item>
            <el-input v-model="username" placeholder="用户名" prefix-icon="el-icon-user" />
          </el-form-item>
          <el-form-item>
            <el-input v-model="password" type="password" placeholder="密码" prefix-icon="el-icon-lock" show-password />
          </el-form-item>
          <el-button type="primary" style="width:100%" :loading="loading" @click="login">
            登录
          </el-button>
        </el-form>
      </el-card>
    </div>
    <el-container v-else class="app-layout" :class="{ 'is-mobile': isMobile }">
      <div v-if="isMobile && sidebarOpen" class="sidebar-mask" @click="closeSidebar" />
      <el-aside width="200px" class="aside" :class="{ open: sidebarOpen }">
        <div class="logo">Localbot</div>
        <div class="user-bar">{{ displayUser }}</div>
        <el-menu :default-active="$route.path" router @select="closeSidebar">
          <el-menu-item index="/users">用户数据</el-menu-item>
          <el-menu-item index="/cities">城市</el-menu-item>
          <el-menu-item index="/main">大分类</el-menu-item>
          <el-menu-item index="/sub">小分类</el-menu-item>
          <el-menu-item index="/merchants">商家</el-menu-item>
          <el-menu-item index="/ads">广告</el-menu-item>
          <el-menu-item index="/logs">操作日志</el-menu-item>
        </el-menu>
        <div class="aside-actions">
          <el-button type="text" class="aside-btn" @click="openStaffDialog">更换客服</el-button>
          <el-button type="text" class="aside-btn" @click="openPasswordDialog">修改密码</el-button>
          <el-button type="text" class="aside-btn logout" @click="logout">退出登录</el-button>
        </div>
      </el-aside>
      <el-container class="main-wrap" direction="vertical">
        <header v-if="isMobile" class="mobile-header">
          <el-button
            type="text"
            icon="el-icon-s-operation"
            class="menu-btn"
            @click="sidebarOpen = true"
          />
          <span class="mobile-title">{{ pageTitle }}</span>
        </header>
        <el-main class="main-content">
          <router-view :key="$route.fullPath" />
        </el-main>
      </el-container>
    </el-container>

    <el-dialog
      title="更换客服"
      :visible.sync="staffVisible"
      :width="isMobile ? '92%' : '400px'"
      @open="loadStaff"
      @closed="staffUsername = ''"
    >
      <el-form @submit.native.prevent="submitStaff">
        <el-form-item label="Telegram 用户名">
          <el-input v-model="staffUsername" placeholder="例如：customer_service（可带 @）" />
        </el-form-item>
        <p class="staff-hint">用户发送 /customerservice 时将收到该用户名</p>
      </el-form>
      <span slot="footer">
        <el-button @click="staffVisible = false">取消</el-button>
        <el-button type="primary" :loading="staffLoading" @click="submitStaff">确定</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="修改密码"
      :visible.sync="pwdVisible"
      :width="isMobile ? '92%' : '400px'"
      @closed="resetPwdForm"
    >
      <el-form @submit.native.prevent="submitPassword">
        <el-form-item label="旧密码">
          <el-input v-model="pwdForm.oldPassword" type="password" show-password autocomplete="current-password" />
        </el-form-item>
        <el-form-item label="新密码">
          <el-input v-model="pwdForm.newPassword" type="password" show-password autocomplete="new-password" />
        </el-form-item>
        <el-form-item label="确认新密码">
          <el-input v-model="pwdForm.confirmPassword" type="password" show-password autocomplete="new-password" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="pwdVisible = false">取消</el-button>
        <el-button type="primary" :loading="pwdLoading" @click="submitPassword">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { api, getToken, getUsername, setAuth, clearToken } from './api';
import responsiveMixin from './mixins/responsive';

const PAGE_TITLES = {
  '/cities': '城市',
  '/main': '大分类',
  '/sub': '小分类',
  '/merchants': '商家',
  '/ads': '广告',
  '/users': '用户数据',
  '/logs': '操作日志',
};

export default {
  name: 'App',
  mixins: [responsiveMixin],
  data() {
    return {
      token: getToken(),
      displayUser: getUsername(),
      username: '',
      password: '',
      loading: false,
      pwdVisible: false,
      pwdLoading: false,
      staffVisible: false,
      staffLoading: false,
      staffUsername: '',
      pwdForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
      sidebarOpen: false,
    };
  },
  computed: {
    pageTitle() {
      return PAGE_TITLES[this.$route.path] || 'Localbot';
    },
  },
  watch: {
    isMobile(mobile) {
      if (!mobile) this.sidebarOpen = false;
    },
  },
  mounted() {
    this.navigateHome();
  },
  methods: {
    closeSidebar() {
      this.sidebarOpen = false;
    },
    openPasswordDialog() {
      this.closeSidebar();
      this.pwdVisible = true;
    },
    openStaffDialog() {
      this.closeSidebar();
      this.staffVisible = true;
    },
    async loadStaff() {
      try {
        const { data } = await api.settings.getStaff();
        this.staffUsername = data.staff || '';
      } catch (e) {
        this.$message.error(e.message);
      }
    },
    async submitStaff() {
      if (!this.staffUsername.trim()) {
        this.$message.warning('请输入 Telegram 用户名');
        return;
      }
      this.staffLoading = true;
      try {
        const { data } = await api.settings.setStaff(this.staffUsername.trim());
        this.$message.success(`客服已更新为 ${data.staff}`);
        this.staffVisible = false;
      } catch (e) {
        this.$message.error(e.message);
      } finally {
        this.staffLoading = false;
      }
    },
    navigateHome() {
      if (!this.token) return;
      if (this.$route.path === '/' || this.$route.path === '/main') {
        this.$router.replace('/users').catch(() => {});
      }
    },
    resetPwdForm() {
      this.pwdForm = { oldPassword: '', newPassword: '', confirmPassword: '' };
    },
    async submitPassword() {
      const { oldPassword, newPassword, confirmPassword } = this.pwdForm;
      if (!oldPassword || !newPassword) {
        this.$message.warning('请填写旧密码和新密码');
        return;
      }
      if (newPassword.length < 6) {
        this.$message.warning('新密码至少 6 位');
        return;
      }
      if (newPassword !== confirmPassword) {
        this.$message.warning('两次输入的新密码不一致');
        return;
      }
      this.pwdLoading = true;
      try {
        const { data } = await api.auth.changePassword(oldPassword, newPassword);
        this.pwdVisible = false;
        this.$message.success(data.zh || '密码已修改，请重新登录');
        clearToken();
        this.token = '';
        this.displayUser = '';
      } catch (e) {
        this.$message.error(e.message);
      } finally {
        this.pwdLoading = false;
      }
    },
    async login() {
      if (!this.username.trim() || !this.password) {
        this.$message.warning('请输入用户名和密码');
        return;
      }
      this.loading = true;
      try {
        const { data } = await api.auth.login(this.username.trim(), this.password);
        setAuth(data.token, data.username);
        this.token = data.token;
        this.displayUser = data.username;
        this.password = '';
        this.navigateHome();
        this.$message.success('登录成功');
      } catch (e) {
        this.$message.error(e.message);
      } finally {
        this.loading = false;
      }
    },
    logout() {
      clearToken();
      this.token = '';
      this.displayUser = '';
      this.username = '';
      this.password = '';
      this.closeSidebar();
    },
  },
};
</script>

<style>
html, body, #app { height: 100%; margin: 0; }
body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
.login-wrap { min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #f0f2f5; padding: 0.43rem; box-sizing: border-box; }
.login-card { width: 9.6rem; }

.app-layout { height: 100vh; overflow: hidden; }
.main-wrap {
  flex: 1;
  min-width: 0;
  height: 100vh;
  overflow: hidden;
}

.sidebar-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 99;
}

.aside {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: 200px !important;
  height: 100vh;
  overflow-y: auto;
  z-index: 100;
  background: #304156;
  color: #fff;
  display: flex;
  flex-direction: column;
  transition: transform 0.25s ease;
}
.logo { padding: 20px; font-size: 18px; font-weight: bold; text-align: center; flex-shrink: 0; }
.user-bar { text-align: center; font-size: 13px; color: #bfcbd9; padding-bottom: 8px; flex-shrink: 0; }
.aside .el-menu { border: none; background: transparent; flex: 1; }
.aside .el-menu-item { color: #bfcbd9; }
.aside .el-menu-item.is-active { color: #409eff; background: #1f2d3d; }
.aside-actions { flex-shrink: 0; padding: 8px 0 16px; text-align: center; }
.aside-btn { color: #bfcbd9; margin: 0 8px; }
.aside-btn.logout { margin: 0; }
.staff-hint { margin: 0; font-size: 12px; color: #909399; line-height: 1.5; }

.main-content {
  margin-left: 200px;
  height: 100vh;
  overflow-y: auto;
  box-sizing: border-box;
}

.mobile-header {
  display: none;
  align-items: center;
  gap: 0.21rem;
  padding: 0 0.32rem;
  height: 1.28rem;
  background: #fff;
  border-bottom: 1px solid #ebeef5;
  flex-shrink: 0;
}
.mobile-header .menu-btn {
  font-size: 0.59rem;
  color: #303133;
  padding: 0.21rem;
}
.mobile-title {
  font-size: 0.43rem;
  font-weight: 600;
  color: #303133;
}

@media (max-width: 767px) {
  .app-layout.is-mobile .aside {
    width: 6.4rem !important;
    transform: translateX(-100%);
  }
  .app-layout.is-mobile .aside.open {
    transform: translateX(0);
  }
  .app-layout.is-mobile .main-wrap {
    width: 100%;
  }
  .app-layout.is-mobile .main-content {
    margin-left: 0;
    height: auto;
    flex: 1;
    overflow-y: auto;
  }
  .app-layout.is-mobile .mobile-header {
    display: flex;
  }
  .app-layout.is-mobile .main-wrap {
    display: flex;
    flex-direction: column;
  }
}
</style>
